import type { ChannelEvent, ChannelSummary, Message, Snapshot, SyncResult, User } from '../api/types'
import { channelTables, db, deleteChannelData, type LocalChannel, type MemberRow } from '../db/db'
import { canPostIn } from '../lib/posting'
import { markAnswered, reduceEvent, targetMessageId } from './reducer'

export type ApplyOutcome = 'applied' | 'duplicate' | 'gap' | 'removed' | 'unknown_channel'

function sameMembers(a: MemberRow[], b: MemberRow[]): boolean {
  return a === b
}

/**
 * Apply one event to a channel row that was already loaded inside the current transaction.
 * Returns the outcome and the updated channel (or null when removed).
 */
async function applyToChannel(
  channel: LocalChannel,
  event: ChannelEvent,
  meId: string,
): Promise<{ outcome: ApplyOutcome; channel: LocalChannel | null }> {
  const messageId = targetMessageId(event)
  const [members, message] = await Promise.all([
    db.members.where('channelId').equals(channel.id).toArray(),
    messageId ? db.messages.get(messageId) : Promise.resolve(undefined),
  ])
  const result = reduceEvent({ channel, members, message }, event, { meId })
  switch (result.status) {
    case 'duplicate':
    case 'gap':
      return { outcome: result.status, channel }
    case 'removed':
      await deleteChannelData(channel.id)
      return { outcome: 'removed', channel: null }
    case 'applied': {
      if (result.message) await db.messages.put(result.message)
      // An answer also locks its question, when this device has it (otherwise it comes hydrated with `answered` when loaded).
      const answer = event.type === 'message.created' ? event.payload.message.answer : null
      if (answer && event.type === 'message.created') {
        const question = await db.messages.get(answer.messageId)
        if (question) {
          const marked = markAnswered(question, event.payload.message)
          if (marked !== question) await db.messages.put(marked)
        }
      }
      if (!sameMembers(result.members, members)) {
        await db.members.where('channelId').equals(channel.id).delete()
        await db.members.bulkPut(result.members)
      }
      await db.channels.put(result.channel)
      if (event.type === 'message.created') {
        // A confirmed send: the outbox row is now represented by the real message.
        await db.outbox.delete(event.payload.message.id)
      }
      return { outcome: 'applied', channel: result.channel }
    }
  }
}

/** Apply live events (hub or REST responses) for a single channel in order, in one transaction. */
export async function applyEvents(events: ChannelEvent[], meId: string): Promise<ApplyOutcome[]> {
  if (events.length === 0) return []
  return db.transaction('rw', channelTables(), async () => {
    const outcomes: ApplyOutcome[] = []
    const byChannel = new Map<string, LocalChannel | null | undefined>()
    for (const event of [...events].sort((a, b) => a.seq - b.seq)) {
      let channel = byChannel.has(event.channelId)
        ? byChannel.get(event.channelId)
        : await db.channels.get(event.channelId)
      if (!channel) {
        outcomes.push('unknown_channel')
        continue
      }
      const { outcome, channel: next } = await applyToChannel(channel, event, meId)
      channel = next
      byChannel.set(event.channelId, channel)
      outcomes.push(outcome)
    }
    return outcomes
  })
}

export function applyEvent(event: ChannelEvent, meId: string): Promise<ApplyOutcome> {
  return applyEvents([event], meId).then((o) => o[0] ?? 'unknown_channel')
}

/** Build a local row from a server summary, keeping client-only fields. */
export function summaryToRow(summary: ChannelSummary, local: LocalChannel | undefined, hasMore?: boolean, summaryHash?: string): LocalChannel {
  const row: LocalChannel = {
    ...summary,
    quietCount: summary.quietCount ?? 0,
    partnerQuiet: summary.partnerQuiet ?? null,
    managed: summary.managed ?? null,
    bot: summary.bot ?? null,
    // Summaries from a server without restricted channels lack these.
    posting: summary.posting ?? 'everyone',
    replyBot: summary.replyBot ?? null,
    memberCount: summary.memberCount ?? summary.memberIds.length,
    lastViewedAt: local?.lastViewedAt ?? null,
    hasMore: hasMore ?? local?.hasMore ?? false,
    summaryHash: summaryHash ?? null,
  }
  if (row.canPost === undefined) row.canPost = canPostIn(row)
  return row
}

async function writeSnapshot(summary: ChannelSummary, snapshot: Snapshot, local: LocalChannel | undefined, summaryHash?: string) {
  const channelId = summary.id
  await db.messages.where('channelId').equals(channelId).delete()
  await db.members.where('channelId').equals(channelId).delete()
  await db.messages.bulkPut(snapshot.messages.map((m) => ({ ...m, channelId: m.channelId || channelId })))
  await db.members.bulkPut(
    snapshot.members.map((m) => ({ channelId, userId: m.userId, role: m.role, source: m.source ?? 'manual', quiet: m.quiet ?? null })),
  )
  await db.channels.put(summaryToRow(summary, local, snapshot.hasMore, summaryHash))
  // Confirmed sends that are part of the snapshot no longer need their outbox row.
  const ids = snapshot.messages.map((m) => m.id)
  if (ids.length) await db.outbox.bulkDelete(ids)
}

/** Whether the caller should request another sync after applying. */
export type SyncApplyOutcome = { needsResync: boolean }

/** Apply one /api/sync result in a single Dexie transaction. */
export async function applySyncResult(result: SyncResult, meId: string): Promise<SyncApplyOutcome> {
  return db.transaction('rw', channelTables(), async () => {
    const local = await db.channels.get(result.id)

    switch (result.status) {
      case 'unchanged':
        // The row is exactly the summary the server would send: nothing to write.
        return { needsResync: false }

      case 'gone':
        await deleteChannelData(result.id)
        return { needsResync: false }

      case 'in_sync': {
        if (local && local.headSeq > result.summary.headSeq) {
          // A live event landed after the server built this response. The local row already includes it and the
          // summary predates it (its role, name or posting could undo the event), so keep the row and sync again.
          return { needsResync: true }
        }
        await db.channels.put(summaryToRow(result.summary, local, undefined, result.summaryHash))
        return { needsResync: false }
      }

      case 'delta': {
        let channel: LocalChannel = local ?? { ...summaryToRow(result.summary, undefined), headSeq: 0, version: null }
        let needsResync = false
        for (const event of [...result.events].sort((a, b) => a.seq - b.seq)) {
          const { outcome, channel: next } = await applyToChannel(channel, event, meId)
          if (outcome === 'removed' || !next) return { needsResync: false }
          if (outcome === 'gap') {
            needsResync = true
            break
          }
          channel = next
        }
        if (channel.headSeq > result.summary.headSeq) {
          // As for in_sync: a live event is newer than this summary, so the row keeps what the events made it.
          return { needsResync: true }
        }
        const row = summaryToRow(result.summary, channel, undefined, result.summaryHash)
        if (channel.headSeq !== result.summary.headSeq) {
          // The row is not the summary as sent, so it has no hash to show for it.
          row.summaryHash = null
          row.headSeq = channel.headSeq
          row.version = channel.version
          if (channel.headSeq < result.summary.headSeq) needsResync = true
        }
        // A delta from the very beginning means the full history is local.
        if (!local || local.headSeq === 0) row.hasMore = false
        await db.channels.put(row)
        return { needsResync }
      }

      case 'resync': {
        if (local && local.version !== null && local.headSeq > result.summary.headSeq) {
          return { needsResync: true }
        }
        await writeSnapshot(result.summary, result.snapshot, local, result.summaryHash)
        return { needsResync: false }
      }
    }
  })
}

export async function upsertUsers(users: User[]): Promise<void> {
  if (users.length) await db.users.bulkPut(users)
}

/**
 * Refresh a locally stored channel from a REST summary (e.g. an admin handing it to an integration). The event head
 * stays where it is, so the events behind the change still apply through sync. Returns false when the
 * channel is not stored on this device.
 */
export async function refreshStoredChannel(summary: ChannelSummary): Promise<boolean> {
  return db.transaction('rw', [db.channels], async () => {
    const local = await db.channels.get(summary.id)
    if (!local) return false
    await db.channels.put({ ...summaryToRow(summary, local), headSeq: local.headSeq, version: local.version })
    return true
  })
}

/** Store older history without clobbering newer local state. */
export async function storeHistory(channelId: string, messages: Message[], hasMore: boolean): Promise<number> {
  return db.transaction('rw', [db.channels, db.messages], async () => {
    const existing = await db.messages.bulkGet(messages.map((m) => m.id))
    const fresh = messages.filter((_, i) => !existing[i]).map((m) => ({ ...m, channelId: m.channelId || channelId }))
    if (fresh.length) await db.messages.bulkPut(fresh)
    await db.channels.update(channelId, { hasMore: hasMore && messages.length > 0 })
    return fresh.length
  })
}
